// src/components/Publications.js
import React from 'react';
import { Box, Typography, Container, Paper, Link } from '@mui/material';
import { motion } from 'framer-motion';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const publications = [
  {
    title: '構造化判断根拠データを用いた視覚言語モデルによる木目官能検査の自動化',
    authors: (
      <>
        <Typography component="span" sx={{ fontWeight: 'bold', color: '#00e5ff' }}>
          岡田壮央
        </Typography>
        ，高井鉄馬，加藤邦人，寺田和憲，篠井暖，塩澤安生
      </>
    ),
    venue: '画像の認識・理解シンポジウム MIRU2026, IS3-191',
    year: '2026.8.6',
  },
  {
    title: '視覚言語モデルによる汎用外観検査の性能向上',
    authors: (
      <>
        土田裕登，表英輝，林良和，上野詩翔，加藤邦人，大澤紘作，鈴木拓実，野村秀一朗，佐々崚介，颯田幹都，桝岡篤人，
        <Typography component="span" sx={{ fontWeight: 'bold', color: '#00e5ff' }}>
          岡田壮央
        </Typography>
        ，髙井鉄馬，早﨑雅人，満仲香苗，梁瀬和哉
      </>
    ),
    venue: '第30回知能メカトロニクスワークショップ iMec2025, IM4-1, pp.128-135',
    year: '2025.9.16',
  },
  {
    title: '特定ドメインにおける視覚言語モデルによる欠陥検出精度評価',
    authors: (
      <>
        <Typography component="span" sx={{ fontWeight: 'bold', color: '#00e5ff' }}>
          岡田壮央
        </Typography>
        ，大澤紘作，尾下拓未，上野詩翔，林良和，中塚俊介，加藤 邦人，相澤宏旭
      </>
    ),
    venue: '第31回画像センシングシンポジウム SSII2025, IS3-14',
    year: '2025.5.30',
    link: 'https://pub.confit.atlas.jp/ja/event/ssii2025/presentation/IS3-14',
  },
];

const Publications = () => {
  return (
    <Box
      id="publications"
      sx={{
        py: { xs: 6, md: 7 },
        position: 'relative',
        zIndex: 1
      }}
    >
      <Container maxWidth="md">
        <motion.div
           initial={{ opacity: 0, y: 16 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ once: true }}
           transition={{ duration: 0.4 }}
        >
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              background: 'linear-gradient(45deg, #00e5ff 30%, #7c4dff 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              fontWeight: 800
            }}
          >
            Publications
          </Typography>
        </motion.div>

        <Typography align="center" color="text.secondary" sx={{ mb: 5 }}>
          視覚言語モデルと外観検査に関する研究業績
        </Typography>

        <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          国内会議
          <Box component="span" sx={{ color: 'primary.main', fontSize: '0.85rem', px: 1.2, py: 0.4, borderRadius: 10, background: 'rgba(0, 229, 255, 0.1)', border: '1px solid rgba(0, 229, 255, 0.25)' }}>
            {publications.length}件
          </Box>
        </Typography>

        <Box sx={{ position: 'relative', pl: { xs: 2.5, sm: 4 } }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '2px',
              background: 'linear-gradient(180deg, rgba(0,229,255,0) 0%, rgba(0,229,255,0.5) 15%, rgba(124,77,255,0.5) 85%, rgba(124,77,255,0) 100%)',
            }}
          />

          {publications.map((pub, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  mb: 2.5,
                  position: 'relative',
                  background: 'rgba(19, 47, 76, 0.38)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 4,
                  transition: 'border-color 0.25s ease, background 0.25s ease',
                  '&:hover': {
                    borderColor: 'rgba(0, 229, 255, 0.35)',
                    background: 'rgba(19, 47, 76, 0.55)'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: 'linear-gradient(180deg, #00e5ff, #7c4dff)'
                  }
                }}
              >
                <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.08em' }}>
                  {pub.year}
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 700 }}>
                  {pub.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                  {pub.authors}
                </Typography>
                <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
                  {pub.venue}
                </Typography>

                {pub.link && (
                  <Link
                    href={pub.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#00e5ff',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#7c4dff'
                      }
                    }}
                  >
                    発表ページ <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                  </Link>
                )}
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Publications;
